import type { IncomingHttpHeaders } from 'http'

import { WP_REST_API_Error, WP_REST_API_Page, WP_REST_API_Post, WP_REST_API_Tags, WP_REST_API_Users } from 'wp-types'

import { GuideType, PageType, PostType } from '@/types/wordpress'

import { cachedFetch } from './cache.service'

type WPRestAPIPostExtended = WP_REST_API_Post & {
  acf?: {
    featured_image_textless?: string
    guide?: any
    short_title?: string
  }
  yoast_head_json: {
    description?: string
    og_image: { url: string }[]
    title?: string
  }
}

const baseUrl = 'https://edhrec.com/articles'
const allowedCookies = ['wordpress_logged_in_', 'wp-rest-auth', 'wordpress_sec_']
const allowedHeaders = ['x-forwarded-for'] // Authorization and Cookies are handled separately

const fetchUrl = async <T>(url: string, options: any): Promise<{ data: T; pageCount: number }> => {
  const headers = Object.fromEntries(
    allowedHeaders
      .filter((header) => options?.headers?.[header as keyof IncomingHttpHeaders])
      .map((header) => [header, options.headers[header as keyof IncomingHttpHeaders]]),
  )

  // Build request headers
  if (options?.cookies) {
    const cookieHeader = Object.entries(options.cookies)
      .filter(([key]) => allowedCookies.some((allowed) => key.startsWith(allowed)))
      .map(([key, value]) => `${key}=${value}`)
      .join('; ')

    if (cookieHeader) {
      headers['Cookie'] = cookieHeader
    }
  }

  let fetcher
  if (options?.token) {
    headers['Authorization'] = `Bearer ${options.token}`
    fetcher = fetch
  } else {
    fetcher = cachedFetch
  }

  // Send request and check status
  const res = await fetcher(url, { headers })
  if (options?.logging) {
    console.log(res)
  }
  if (res.status === 400) {
    const error = (await res.json()) as WP_REST_API_Error
    throw new Error(error.message)
  } else if (res.status === 401) {
    throw new Error('Unauthorized')
  } else if (res.status === 404) {
    throw new Error(`Not found: ${url}`)
  }

  // Log response headers
  // console.log('Response headers', res.headers)

  const data: T = await res.json()

  // Check for valid response
  if ((data as WP_REST_API_Error).data?.status === 403) {
    throw new Error('Signature verification failed')
  }

  const pageCount = parseInt(res.headers.get('X-WP-TotalPages') || '1', 10)

  return { data, pageCount }
}

export const formatTag = (tag: string): string => tag.replace(/-/g, ' ').replace('#', '').toLowerCase()

const getAuthorId = async (author: string, options?: RequestInit): Promise<number | undefined> => {
  const url = `${baseUrl}/wp-json/wp/v2/users?slug=${author}`
  const { data: authors } = await fetchUrl<WP_REST_API_Users>(url, options)
  return authors[0]?.id
}

export const getPage = async ({
  id,
  options,
  slug,
  token,
}: {
  id?: string
  options?: RequestInit
  slug?: string
  token?: string
}): Promise<PageType | undefined> => {
  const url = `${baseUrl}/wp-json/wp/v2/pages${slug ? `?slug=${slug}&_embed` : `/${id}/revisions?skip_cache=1`}`
  const { data: pages } = await fetchUrl<WPRestAPIPostExtended[]>(url, { ...options, token })
  if (pages.length === 0) return undefined
  const page = pages[0]

  // The revisions endpoint doesn't include embeds or ACF data, so we need to fetch those separately
  if (id) {
    const { data: pageWithEmbeds } = await fetchUrl<WPRestAPIPostExtended>(
      `${baseUrl}/wp-json/wp/v2/pages/${id}?_embed`,
      { ...options, token },
    )
    if (!pageWithEmbeds) return undefined
    page._embedded = pageWithEmbeds._embedded
    page.acf = pageWithEmbeds.acf // Requires ACF to REST API plugin
  }

  return parsePage(page)
}

export const getPost = async ({
  cookies,
  id,
  options,
  slug,
  token,
}: {
  cookies?: any
  id?: string
  options?: RequestInit
  slug?: string
  token?: string
}): Promise<PostType | undefined> => {
  const url = id
    ? `${baseUrl}/wp-json/wp/v2/posts/${id}/revisions?skip_cache=1`
    : `${baseUrl}/wp-json/wp/v2/posts?slug=${slug}&_embed`
  const { data: posts } = await fetchUrl<WPRestAPIPostExtended[]>(url, { cookies, ...options, token })

  if (posts.length === 0) return undefined
  const post = posts[0]

  // The revisions endpoint doesn't include embeds, so we need to fetch those separately
  if (id) {
    const { data: postWithEmbeds } = await fetchUrl<WPRestAPIPostExtended>(
      `${baseUrl}/wp-json/wp/v2/posts/${id}?_embed`,
      {
        ...options,
        cookies,
        token,
      },
    )
    if (!postWithEmbeds) return undefined
    post._embedded = postWithEmbeds._embedded
  }

  return parsePost(post)
}

export const getPosts = async ({
  author,
  categories,
  options,
  page = 1,
  search,
  tag,
}: {
  author?: string
  categories?: number[]
  options?: RequestInit
  page: number
  search?: string
  tag?: string
}) => {
  if (isNaN(page) || !Number.isInteger(page) || page < 1) {
    throw new Error('Page must be a positive integer')
  }

  const params = new URLSearchParams({
    _embed: '',
    page: `${page}`,
  })

  const errorResponse = {
    pageCount: 0,
    posts: [],
  }

  if (author) {
    const authorId = await getAuthorId(author, options)
    if (!authorId) return errorResponse
    params.append('author', `${authorId}`)
  }

  if (categories) {
    params.append('categories', categories.join(','))
  }

  if (search) {
    params.append('search', search)
  }

  if (tag) {
    const tagId = await getTagId(tag, options)
    if (!tagId) return errorResponse
    params.append('tags', `${tagId}`)
  }

  const url = `${baseUrl}/wp-json/wp/v2/posts?${params.toString()}`

  const { data: rawPosts, pageCount } = await fetchUrl<WPRestAPIPostExtended[]>(url, options)

  const posts = rawPosts.map(parsePost)

  return { pageCount, posts }
}

const getTagId = async (tag: string, options?: RequestInit): Promise<number | undefined> => {
  const url = `${baseUrl}/wp-json/wp/v2/tags?slug=${tag}`
  const { data: tags } = await fetchUrl<WP_REST_API_Tags>(url, options)
  return tags[0]?.id
}

export const login = async (username: string, password: string) => {
  const res = await fetch('https://edhrec.com/articles/wp-json/jwt-auth/v1/token', {
    body: JSON.stringify({ password, username }),
    headers: {
      'Content-Type': 'application/json',
    },
    method: 'POST',
  })

  if (!res.ok) {
    throw new Error('Login failed')
  }

  const data = await res.json()

  return data
}

const parseExcerpt = (content: string): string => {
  const match = content.match(/^<p>(.*?)<\/p>\n$/)
  return match ? match[1] : content
}

export const parsePage = (page: WP_REST_API_Page): PageType => ({
  content: page.content.rendered,
  title: page.title.rendered,
})

export const parsePost = (post: WPRestAPIPostExtended): PostType => {
  const { _embedded } = post as any
  const rawCategories = _embedded['wp:term'].filter((term: any) => term[0]?.taxonomy === 'category')[0]
  const rawTags = _embedded['wp:term'].filter((term: any) => term[0]?.taxonomy === 'post_tag')[0]
  const acf = post.acf
  const guide = acf?.guide as GuideType | undefined
  const shortTitle = acf?.short_title
  const featuredImageUrl = post.yoast_head_json?.og_image?.[0]?.url || _embedded['wp:featuredmedia']?.[0]?.source_url
  const featuredImageTextlessUrl = acf?.featured_image_textless

  const parsedPost: PostType = {
    author: {
      avatarUrl: _embedded.author[0].avatar_urls['96'],
      description: _embedded.author[0].description,
      name: _embedded.author[0].name,
      slug: _embedded.author[0].slug,
    },
    categories: rawCategories ? rawCategories.map(({ slug }: { slug: string }) => slug) : [],
    content: post.content.rendered,
    databaseId: `${post.id}`,
    date: post.date,
    excerpt: parseExcerpt(post.excerpt.rendered),
    slug: post.slug,
    tags: rawTags ? rawTags.map(({ name, slug }: { name: string; slug: string }) => ({ name, slug })) : [],
    title: post.title.rendered,
  }

  if (featuredImageUrl) parsedPost.featuredImageUrl = featuredImageUrl
  if (featuredImageTextlessUrl) parsedPost.featuredImageTextlessUrl = featuredImageTextlessUrl
  if (guide) parsedPost.guide = guide
  if (shortTitle) parsedPost.shortTitle = shortTitle

  return parsedPost
}
